import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, RefreshCw, Gift, HelpCircle, Check, AlertCircle, MessageCircle, Gem, Package } from 'lucide-react';
import { toast } from 'sonner';
import Header from '@/components/Header';
import ProgressBar from '@/components/ProgressBar';
import { Button } from '@/components/ui/button';
import { getPublicSettings } from '@/lib/api';
import { cn } from '@/lib/utils';

const BRIDAL_KEYWORDS = ['abito da sposa', 'collezione sposa', 'abiti sposa', 'bride', 'bridal', 'wedding dress'];

const isBridalItem = (item, overrides = []) => {
  if (overrides && overrides.length > 0) {
    const itemProductId = String(item.product_id || '');
    const override = overrides.find(o => String(o.product_id) === itemProductId);
    if (override) return override.force_category === 'bridal';
  }
  const title = (item.title || '').toLowerCase();
  const productType = (item.product_type || '').toLowerCase();
  const tags = (item.tags || '').toLowerCase();
  return BRIDAL_KEYWORDS.some(kw => title.includes(kw) || productType.includes(kw) || tags.includes(kw));
};

const RequestType = () => {
  const { orderNumber } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [requestType, setRequestType] = useState(null);
  const [services, setServices] = useState({
    service_exchange_enabled: true,
    service_voucher_enabled: true,
    service_rma_enabled: true,
    voucher_bonus_percent: 10,
    shipping_cost_base: 35,
  });
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  useEffect(() => {
    const storedOrder = sessionStorage.getItem('currentOrder');
    const storedItems = sessionStorage.getItem('selectedItems');
    
    if (storedOrder && storedItems) {
      setOrder(JSON.parse(storedOrder));
      setSelectedItems(JSON.parse(storedItems));
    } else {
      navigate('/');
    }

    // Load public settings
    getPublicSettings().then(data => {
      setServices(data);
      setSettingsLoaded(true);
    }).catch(() => setSettingsLoaded(true));
  }, [navigate]);

  const handleContinue = async () => {
    if (!requestType) {
      toast.error('Seleziona il tipo di richiesta');
      return;
    }
    
    sessionStorage.setItem('requestType', requestType);
    
    if (requestType === 'exchange') {
      navigate(`/order/${orderNumber}/exchange`);
    } else if (requestType === 'rma') {
      // RMA flow (full card style)
      sessionStorage.setItem('requestType', 'return');
      navigate(`/order/${orderNumber}/rma/step1`);
    } else {
      // For voucher, go to summary
      sessionStorage.removeItem('exchangeItems');
      navigate(`/order/${orderNumber}/summary`);
    }
  };

  const handleOtherRequest = () => {
    // "Ho un'altra richiesta" leads to RMA flow (reso)
    sessionStorage.setItem('requestType', 'return');
    navigate(`/order/${orderNumber}/rma/step1`);
  };

  if (!order) {
    return null;
  }

  // For exchange orders, use original_order_total instead of summing discounted prices
  const currentOrder = JSON.parse(sessionStorage.getItem('currentOrder') || '{}');
  const isExchangeOrder = currentOrder?.is_exchange_order;
  const rawTotal = selectedItems.reduce((sum, item) => sum + parseFloat(item.price), 0);
  const totalValue = isExchangeOrder && currentOrder?.original_order_total 
    ? parseFloat(currentOrder.original_order_total) 
    : rawTotal;
  const bonusPercent = services.voucher_bonus_percent || 0;
  const hasBonus = bonusPercent > 0;
  const paidWithGiftCard = order.paid_with_gift_card || false;

  // Calcola costo gestione reso in base ai prezzi degli articoli
  const calculateReturnCostFromItems = (items, settings) => {
    const threshold = settings.shipping_price_threshold || 249;
    const firstBelow = settings.shipping_cost_first_below || 35;
    const firstAbove = settings.shipping_cost_first_above || 50;
    const addBelow = settings.shipping_cost_additional_below || 15;
    const addAbove = settings.shipping_cost_additional_above || 25;
    const sorted = [...items].sort((a, b) => parseFloat(b.price) - parseFloat(a.price));
    let cost = 0;
    sorted.forEach((item, i) => {
      const price = parseFloat(item.price);
      if (i === 0) {
        cost += price >= threshold ? firstAbove : firstBelow;
      } else {
        cost += price >= threshold ? addAbove : addBelow;
      }
    });
    return cost;
  };

  const returnCostForGiftCard = calculateReturnCostFromItems(selectedItems, services);

  // Calcolo valore voucher
  let voucherValue;
  if (paidWithGiftCard) {
    voucherValue = Math.max(totalValue - returnCostForGiftCard, 0);
  } else {
    voucherValue = totalValue * (1 + bonusPercent / 100);
  }

  const exchangeEnabled = services.service_exchange_enabled;
  const voucherEnabled = services.service_voucher_enabled;
  const rmaEnabled = services.service_rma_enabled;
  const exchangeFee = services.exchange_fee || 20;

  // Bridal detection
  const hasBridalItem = selectedItems.some(item => isBridalItem(item, services.product_category_overrides));
  const bridalWhatsappEnabled = services.bridal_whatsapp_enabled;
  const bridalReturnCost = services.bridal_return_cost || 100;
  const showBridalBlock = hasBridalItem && bridalWhatsappEnabled;

  // Per ordini pagati con gift card, il reso monetario non è disponibile
  const rmaAvailable = rmaEnabled && !paidWithGiftCard;
  const rmaButtonStyle = services.rma_button_style || 'minimal';

  // Count active card services (exchange + voucher + RMA if full style)
  const rmaIsCard = rmaAvailable && rmaButtonStyle === 'full';
  const activeCards = [exchangeEnabled, voucherEnabled, rmaIsCard].filter(Boolean).length;
  const gridCols = activeCards === 1 ? 'md:grid-cols-1 max-w-lg mx-auto' : activeCards === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2';

  return (
    <div className="page-container bg-white">
      <Header showBack backTo={`/order/${orderNumber}/select`} />
      <ProgressBar currentStep={2} totalSteps={5} />
      
      <main className="flex-1 py-12 px-6">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-muted-foreground mb-4">
              Passaggio 2 di 5
            </p>
            <h1 className="font-heading text-3xl md:text-4xl font-normal tracking-tight mb-2">
              Cosa preferisci?
            </h1>
            <p className="text-muted-foreground font-light mb-12">
              Scegli come desideri procedere con la tua richiesta
            </p>
          </motion.div>

          {/* Bridal WhatsApp Block */}
          {showBridalBlock && (
            <motion.div
              className="mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              data-testid="bridal-whatsapp-block"
            >
              <div className="bg-pink-50 border border-pink-200 rounded-xl p-8 text-center">
                <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-5">
                  <Gem className="w-8 h-8 text-pink-500" />
                </div>
                <h2 className="font-heading text-2xl mb-3 text-pink-900">Collezione Sposa</h2>
                <p className="text-sm text-pink-800 leading-relaxed mb-4 max-w-md mx-auto"
                   dangerouslySetInnerHTML={{ __html: (services?.cost_explanations?.bridal_intro || 'Il tuo ordine contiene un abito della collezione sposa. Il costo minimo di gestione del reso è di €{bridal_cost}, in quanto i tessuti pregiati degli abiti da sposa richiedono trattamenti specializzati e più costosi.').replace('{bridal_cost}', `<strong>&euro;${(services?.bridal_return_cost || bridalReturnCost).toFixed(2)}</strong>`) }}
                />
                <p className="text-sm text-pink-700 mb-6 max-w-md mx-auto">
                  {services?.cost_explanations?.bridal_whatsapp_text || 'Per procedere con la tua richiesta, ti chiediamo di contattarci direttamente su WhatsApp. Il nostro team dedicato alla collezione sposa ti assisterà personalmente.'}
                </p>
                <a
                  href="https://wa.me/393383349075"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-[#25D366] hover:bg-[#20bd5a] text-white font-semibold px-8 py-4 rounded-full transition-colors text-base shadow-lg"
                  data-testid="bridal-whatsapp-btn"
                >
                  <MessageCircle className="w-5 h-5" />
                  Contattaci su WhatsApp
                </a>
              </div>
            </motion.div>
          )}

          {/* Gift Card Payment Banner */}
          {!showBridalBlock && paidWithGiftCard && (
            <motion.div
              className="bg-amber-50 border border-amber-300 p-4 mb-8 flex items-start gap-3"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.05 }}
              data-testid="gift-card-banner"
            >
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-900">
                  Ordine pagato con buono/gift card
                </p>
                <p className="text-xs text-amber-700 mt-1">
                  Poiché questo ordine è stato pagato con un credito negozio o gift card, il rimborso monetario non è disponibile. 
                  Puoi richiedere un cambio o un voucher (al netto dei costi di gestione di &euro;{returnCostForGiftCard.toFixed(2)}).
                </p>
              </div>
            </motion.div>
          )}

          {/* Selected Items Summary */}
          <motion.div 
            className="luxury-card p-6 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <p className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground mb-4">
              Prodotti Selezionati
            </p>
            <div className="space-y-3">
              {selectedItems.map((item, index) => (
                <div key={item.unique_id || index} className="flex justify-between items-center">
                  <span className="text-sm">{item.title} - {item.variant_title}</span>
                  {!isExchangeOrder && (
                    <span className="text-sm font-medium">&euro;{parseFloat(item.price).toFixed(2)}</span>
                  )}
                </div>
              ))}
              <div className="border-t border-border pt-3 mt-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">{isExchangeOrder ? 'Importo ordine' : 'Totale'}</span>
                  <span className="text-lg font-medium">&euro;{totalValue.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Main Options */}
          {!showBridalBlock && (exchangeEnabled || voucherEnabled) && (
            <motion.div 
              className={`grid ${gridCols} gap-6 mb-6`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              {/* CAMBIO */}
              {exchangeEnabled && (
                <button
                  onClick={() => setRequestType('exchange')}
                  className={cn(
                    "luxury-card p-8 text-left transition-all hover:border-primary/50 relative",
                    requestType === 'exchange' 
                      ? "border-2 border-primary bg-primary/5 shadow-lg ring-2 ring-primary/20" 
                      : "border border-border"
                  )}
                  data-testid="exchange-option"
                >
                  {requestType === 'exchange' && (
                    <div className="absolute top-4 right-4 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  )}
                  <RefreshCw className={cn("w-8 h-8 mb-4", requestType === 'exchange' && "text-primary")} />
                  <h3 className="font-heading text-xl mb-3">Cambio</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {(() => {
                      const method = services?.shipping_method_exchange || 'pickup';
                      if (method === 'pickup') return 'Organizzeremo il ritiro del tuo pacco e, dopo aver effettuato il controllo qualità, provvederemo a spedirti il nuovo capo da te selezionato.';
                      return 'Spedisci il pacco al nostro indirizzo e, dopo aver effettuato il controllo qualità, provvederemo a spedirti il nuovo capo da te selezionato.';
                    })()}
                  </p>
                  <p className="text-xs text-muted-foreground mt-3 pt-3 border-t border-border">
                    Costo gestione: &euro;{exchangeFee.toFixed(2)}
                  </p>
                </button>
              )}
              
              {/* VOUCHER */}
              {voucherEnabled && (
                <button
                  onClick={() => setRequestType('voucher')}
                  className={cn(
                    "luxury-card p-8 text-left transition-all hover:border-emerald-300 relative",
                    requestType === 'voucher' 
                      ? "border-2 border-emerald-500 bg-emerald-50 shadow-lg ring-2 ring-emerald-200" 
                      : "border border-border"
                  )}
                  data-testid="voucher-option"
                >
                  {requestType === 'voucher' && (
                    <div className="absolute top-4 right-4 w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  )}
                  <Gift className={cn("w-8 h-8 mb-4", requestType === 'voucher' && "text-emerald-600")} />
                  <h3 className="font-heading text-xl mb-3">Voucher</h3>

                  {paidWithGiftCard ? (
                    /* Gift card order: no bonus, show deduction */
                    <>
                      <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                        Ricevi un voucher spendibile sul nostro sito, al netto dei costi di gestione del reso.
                      </p>
                      <div className="bg-amber-50 border border-amber-200 p-4 -mx-2">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-muted-foreground uppercase tracking-wider">Valore articoli</span>
                          <span className="text-sm text-muted-foreground">&euro;{totalValue.toFixed(2)}</span>
                        </div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-muted-foreground uppercase tracking-wider">Costi di gestione</span>
                          <span className="text-sm text-red-600">-&euro;{returnCostForGiftCard.toFixed(2)}</span>
                        </div>
                        <div className="border-t border-amber-200 pt-2 flex items-center justify-between">
                          <span className="text-xs text-emerald-600 uppercase tracking-wider font-medium">Voucher di</span>
                          <span className="text-2xl font-bold text-emerald-700">&euro;{voucherValue.toFixed(2)}</span>
                        </div>
                      </div>
                    </>
                  ) : hasBonus ? (
                    /* Normal order with bonus > 0: show bonus overlay */
                    <>
                      <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                        Ricevi un voucher spendibile sul nostro sito, pari al {100 + bonusPercent}% del valore del tuo ordine originale.
                      </p>
                      <div className="bg-emerald-50 border border-emerald-200 p-4 -mx-2">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-xs text-muted-foreground uppercase tracking-wider">Prima</span>
                          <span className="text-lg text-muted-foreground line-through">&euro;{totalValue.toFixed(2)}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-emerald-600 uppercase tracking-wider font-medium">Voucher di</span>
                          <span className="text-3xl font-bold text-emerald-700">&euro;{voucherValue.toFixed(2)}</span>
                        </div>
                        <p className="text-xs text-emerald-600 mt-2 text-right">+{bonusPercent}% di bonus</p>
                      </div>
                    </>
                  ) : (
                    /* Normal order with bonus = 0: simple display, no bonus overlay */
                    <>
                      <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                        Ricevi un voucher spendibile sul nostro sito, pari al valore del tuo ordine originale.
                      </p>
                      <div className="mt-2 pt-3 border-t border-border">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-emerald-600 uppercase tracking-wider font-medium">Voucher di</span>
                          <span className="text-2xl font-bold text-emerald-700">&euro;{voucherValue.toFixed(2)}</span>
                        </div>
                      </div>
                    </>
                  )}
                </button>
              )}

              {/* RESO (full card style) */}
              {rmaIsCard && (
                <button
                  onClick={() => { setRequestType('rma'); }}
                  className={cn(
                    "luxury-card p-8 text-left transition-all hover:border-amber-300 relative",
                    requestType === 'rma' 
                      ? "border-2 border-amber-500 bg-amber-50 shadow-lg ring-2 ring-amber-200" 
                      : "border border-border"
                  )}
                  data-testid="rma-option"
                >
                  {requestType === 'rma' && (
                    <div className="absolute top-4 right-4 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  )}
                  <Package className={cn("w-8 h-8 mb-4", requestType === 'rma' && "text-amber-600")} />
                  <h3 className="font-heading text-xl mb-3">Reso</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Restituisci il prodotto e ricevi un rimborso monetario, al netto dei costi di gestione del reso.
                  </p>
                  <p className="text-xs text-muted-foreground mt-3 pt-3 border-t border-border">
                    Rimborso al netto dei costi di gestione
                  </p>
                </button>
              )}
            </motion.div>
          )}

          {/* "Ho un'altra richiesta" - leads to RMA/RESO flow (minimal style only) */}
          {!showBridalBlock && rmaAvailable && rmaButtonStyle === 'minimal' && (
            <motion.div
              className="text-center mb-10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.25 }}
            >
              <button
                onClick={handleOtherRequest}
                className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors px-4 py-2"
                data-testid="other-request-link"
              >
                <HelpCircle className="w-4 h-4" />
                <span className="underline underline-offset-4">
                  Ho un'altra richiesta
                </span>
              </button>
            </motion.div>
          )}

          {/* Action Button - show only if exchange, voucher, or RMA card enabled */}
          {!showBridalBlock && (exchangeEnabled || voucherEnabled || rmaIsCard) && (
            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <Button 
                onClick={handleContinue}
                disabled={!requestType}
                className="luxury-btn-primary inline-flex items-center gap-3"
                data-testid="continue-type-btn"
              >
                {requestType === 'exchange' ? 'Scegli il Nuovo Prodotto' : requestType === 'rma' ? 'Procedi con il Reso' : 'Continua'}
                <ArrowRight className="w-4 h-4" />
              </Button>
            </motion.div>
          )}
        </div>
      </main>
    </div>
  );
};

export default RequestType;
